import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { getBackendUrl } from '../utils/api';
import storage from '../utils/storage';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { 
  Settings, 
  Music, 
  Radio, 
  Mail, 
  Download, 
  Save, 
  RefreshCw, 
  Plus, 
  Trash2, 
  AlertCircle,
  CheckCircle,
  Loader2,
  Upload,
  Database
} from 'lucide-react';
import { toast } from 'sonner';
import ValidationPanel from './StreamMonitor/ValidationPanel';
import FirstPlayAlertsPanel from './FirstPlayAlerts/FirstPlayAlertsPanel';

const BACKEND_URL = getBackendUrl();

// ═══════════════════════════════════════════════════════════════
// SONG YEAR MANAGER - Extracted to prevent re-mounting on parent re-render
// ═══════════════════════════════════════════════════════════════
const SongYearManager = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [editingSong, setEditingSong] = useState(null);
  const [newYear, setNewYear] = useState('');

  const getAuthHeaders = () => {
    const token = storage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/admin/config/song-years/search?query=${encodeURIComponent(searchQuery)}`,
        getAuthHeaders()
      );
      setSearchResults(res.data.results || []);
      if (res.data.results?.length === 0) {
        toast.info('Δεν βρέθηκαν αποτελέσματα');
      }
    } catch (err) {
      toast.error('Σφάλμα αναζήτησης: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSearching(false);
    }
  };

  const handleUpdateYear = async (song) => {
    if (!newYear || isNaN(parseInt(newYear))) {
      toast.error('Παρακαλώ εισάγετε έγκυρο έτος');
      return;
    }
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/config/song-years/update`,
        { artist: song.artist, title: song.title, year: parseInt(newYear) },
        getAuthHeaders()
      );
      toast.success(`Το έτος ενημερώθηκε σε ${newYear}`);
      setEditingSong(null);
      setNewYear('');
      handleSearch(); // Refresh results
    } catch (err) {
      toast.error('Σφάλμα ενημέρωσης: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleClearCache = async (song) => {
    if (!window.confirm(`Καθαρισμός cache για "${song.artist} - ${song.title}";`)) return;
    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/config/song-years/clear-cache?artist=${encodeURIComponent(song.artist)}&title=${encodeURIComponent(song.title)}`,
        getAuthHeaders()
      );
      toast.success('Το cache καθαρίστηκε. Το επόμενο play θα κάνει re-lookup.');
      handleSearch();
    } catch (err) {
      toast.error('Σφάλμα: ' + (err.response?.data?.detail || err.message));
    }
  };

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="flex gap-2">
        <Input
          placeholder="Αναζήτηση καλλιτέχνη ή τίτλου..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          className="flex-1"
          data-testid="song-year-search-input"
        />
        <Button onClick={handleSearch} disabled={searching} data-testid="song-year-search-btn">
          {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Αναζήτηση
        </Button>
      </div>

      {/* Results */}
      {searchResults.length > 0 && (
        <div className="border rounded-lg dark:border-gray-700">
          <div className="p-3 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <span className="text-sm text-gray-600 dark:text-gray-300">
              Βρέθηκαν {searchResults.length} αποτελέσματα
            </span>
          </div>
          <ScrollArea className="h-[300px]">
            <div className="divide-y dark:divide-gray-700">
              {searchResults.map((song, idx) => (
                <div key={idx} className="p-3 flex justify-between items-center hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 dark:text-white">
                      {song.artist} - {song.title}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <Badge variant={song.source === 'local_db' ? 'secondary' : 'outline'}>
                        {song.source === 'local_db' ? 'Local DB' : 'MongoDB Cache'}
                      </Badge>
                      {song.year && (
                        <span className={song.year < 2020 ? 'text-yellow-600' : 'text-green-600'}>
                          Έτος: {song.year}
                        </span>
                      )}
                      {song.duration && <span>Διάρκεια: {song.duration}s</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {editingSong === idx ? (
                      <>
                        <Input
                          type="number"
                          placeholder="Νέο έτος"
                          value={newYear}
                          onChange={(e) => setNewYear(e.target.value)}
                          className="w-24 h-8"
                          data-testid={`song-year-input-${idx}`}
                        />
                        <Button size="sm" onClick={() => handleUpdateYear(song)} data-testid={`song-year-save-${idx}`}>
                          <Save className="h-3 w-3" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => { setEditingSong(null); setNewYear(''); }}>
                          ✕
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => { setEditingSong(idx); setNewYear(song.year?.toString() || ''); }}
                          data-testid={`song-year-edit-${idx}`}
                        >
                          Διόρθωση
                        </Button>
                        {song.source === 'mongodb_cache' && (
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => handleClearCache(song)}
                            className="text-red-500"
                            data-testid={`song-year-clear-${idx}`}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      )}

      <div className="text-xs text-gray-500 p-2 bg-gray-50 dark:bg-gray-800/50 rounded">
        <AlertCircle className="h-4 w-4 inline mr-1" />
        <strong>Tip:</strong> Αν διορθώσετε έτος στο MongoDB Cache, θα εφαρμοστεί στις επόμενες αναφορές.
        Το Local DB (user_songs_db.json) δεν μπορεί να τροποποιηθεί από εδώ.
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// YEAR CLEANUP BUTTON - Fix incorrect 2020 years
// ═══════════════════════════════════════════════════════════════
const YearCleanupButton = () => {
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const getAuthHeaders = () => {
    const token = storage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const handlePreview = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/system/fix-2020-years?dry_run=true`,
        {},
        getAuthHeaders()
      );
      setPreviewData(res.data);
      if (res.data.songs_to_fix > 0) {
        setShowConfirm(true);
      } else {
        toast.success('Δεν βρέθηκαν τραγούδια με λάθος έτος 2020!');
      }
    } catch (err) {
      toast.error('Σφάλμα: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleExecute = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/system/fix-2020-years?dry_run=false`,
        {},
        getAuthHeaders()
      );
      toast.success(`✅ Διορθώθηκαν ${res.data.total_fixed} τραγούδια!`);
      setShowConfirm(false);
      setPreviewData(null);
    } catch (err) {
      toast.error('Σφάλμα: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-3">
        <Button
          onClick={handlePreview}
          disabled={loading}
          variant="outline"
          className="border-orange-300 text-orange-600 hover:bg-orange-50"
          data-testid="preview-2020-cleanup"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <AlertCircle className="h-4 w-4 mr-2" />
          )}
          Προεπισκόπηση
        </Button>
      </div>

      {/* Confirmation Dialog */}
      {showConfirm && previewData && (
        <div className="p-4 border rounded-lg bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800">
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle className="h-5 w-5 text-orange-500" />
            <span className="font-medium text-orange-700 dark:text-orange-300">
              Βρέθηκαν {previewData.songs_to_fix} τραγούδια με λάθος έτος 2020
            </span>
          </div>
          
          {previewData.sample_songs?.length > 0 && (
            <div className="mb-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Παραδείγματα:</p>
              <ul className="text-sm text-gray-500 dark:text-gray-400 list-disc list-inside max-h-32 overflow-y-auto">
                {previewData.sample_songs.map((song, i) => (
                  <li key={i} className="truncate">{song}</li>
                ))}
              </ul>
            </div>
          )}
          
          <div className="flex gap-3">
            <Button
              onClick={handleExecute}
              disabled={loading}
              variant="destructive"
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="execute-2020-cleanup"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              Εκτέλεση Καθαρισμού
            </Button>
            <Button
              onClick={() => setShowConfirm(false)}
              variant="outline"
              disabled={loading}
            >
              Ακύρωση
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// CANONICAL NAMES BUTTON - Populate display names from enrichment DB
// With background task polling for apply operation
// ═══════════════════════════════════════════════════════════════
const CanonicalNamesButton = () => {
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [stats, setStats] = useState(null);
  const [applyStats, setApplyStats] = useState(null);
  const [taskStatus, setTaskStatus] = useState(null);
  const [autoApply, setAutoApply] = useState(true); // Auto-apply after update
  const pollIntervalRef = useRef(null);

  const getAuthHeaders = () => {
    const token = storage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  // Poll status - needs to be before triggerApply
  const pollStatus = useCallback(async () => {
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/admin/system/apply-canonical-names/status`,
        getAuthHeaders()
      );
      setTaskStatus(res.data);
      
      // Check if task completed
      if (!res.data.running && res.data.completed_at) {
        // Stop polling
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        setApplying(false);
        
        if (res.data.error) {
          toast.error(`❌ Σφάλμα: ${res.data.error}`);
        } else {
          setApplyStats({ updated: res.data.updated, total_without_canonical: 0 });
          toast.success(`✅ Ολοκληρώθηκε! Ενημερώθηκαν ${res.data.updated} τραγούδια!`);
        }
      }
    } catch (err) {
      console.error('Status poll error:', err);
    }
  }, []);

  // Trigger apply after successful populate
  const triggerApply = useCallback(async () => {
    setApplying(true);
    setTaskStatus(null);
    setApplyStats(null);
    
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/system/apply-canonical-names?dry_run=false`,
        {},
        { ...getAuthHeaders(), timeout: 30000 }
      );
      
      if (res.data.success) {
        toast.info('🔄 Αυτόματη εφαρμογή ξεκίνησε...', { duration: 3000 });
        pollIntervalRef.current = setInterval(pollStatus, 2000);
        pollStatus();
      } else {
        setApplying(false);
        toast.error('Σφάλμα: ' + (res.data.error || 'Άγνωστο σφάλμα'));
      }
    } catch (err) {
      setApplying(false);
      toast.error('Σφάλμα αυτόματης εφαρμογής: ' + err.message);
    }
  }, [pollStatus]);

  const handlePopulate = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/system/populate-canonical-names`,
        {},
        getAuthHeaders()
      );
      if (res.data.success) {
        setStats(res.data);
        toast.success(`✅ Δημιουργήθηκαν ${res.data.artists_added} canonical names!`);
        
        // AUTO APPLY if enabled and there are pending songs
        if (autoApply && res.data.songs_pending_canonical > 0) {
          toast.info('🔄 Αυτόματη εφαρμογή σε τραγούδια...', { duration: 2000 });
          setTimeout(() => triggerApply(), 1000);
        }
      } else {
        toast.error('Σφάλμα: ' + res.data.error);
      }
    } catch (err) {
      toast.error('Σφάλμα: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleApplyToSongs = async () => {
    setApplying(true);
    setTaskStatus(null);
    setApplyStats(null);
    
    try {
      // Start the background task
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/system/apply-canonical-names?dry_run=false`,
        {},
        { 
          ...getAuthHeaders(),
          timeout: 30000 // 30 second timeout for starting
        }
      );
      
      if (res.data.success) {
        toast.info('🔄 Η διεργασία ξεκίνησε. Παρακολουθείτε την πρόοδο...', { duration: 3000 });
        
        // Start polling for status
        pollIntervalRef.current = setInterval(pollStatus, 2000); // Poll every 2 seconds
        
        // Initial status fetch
        pollStatus();
      } else {
        setApplying(false);
        toast.error('Σφάλμα: ' + (res.data.error || 'Άγνωστο σφάλμα'));
      }
    } catch (err) {
      setApplying(false);
      const errorMsg = err.code === 'ECONNABORTED' 
        ? 'Timeout κατά την εκκίνηση' 
        : (err.response?.data?.detail || err.message);
      toast.error('Σφάλμα: ' + errorMsg);
    }
  };

  return (
    <div className="space-y-4">
      {/* Auto-apply toggle */}
      <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600 dark:text-gray-300">
            Αυτόματη εφαρμογή μετά την ενημέρωση
          </span>
          <span className="text-xs text-gray-500">(Συνίσταται)</span>
        </div>
        <Switch 
          checked={autoApply} 
          onCheckedChange={setAutoApply}
          data-testid="auto-apply-toggle"
        />
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          onClick={handlePopulate}
          disabled={loading || applying}
          className="bg-teal-600 hover:bg-teal-700 text-white"
          data-testid="populate-canonical-names"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          {autoApply ? 'Ενημέρωση & Εφαρμογή' : '1. Ενημέρωση Database'}
        </Button>
        
        {!autoApply && (
          <Button
            onClick={handleApplyToSongs}
            disabled={loading || applying}
            className="bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="apply-canonical-names"
          >
            {applying ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Database className="h-4 w-4 mr-2" />
            )}
            2. Εφαρμογή στα Τραγούδια
          </Button>
        )}
      </div>
      
      {/* Scheduler info */}
      <div className="text-xs text-gray-500 dark:text-gray-400 p-2 bg-gray-50 dark:bg-gray-800/30 rounded">
        <AlertCircle className="h-3 w-3 inline mr-1" />
        <strong>Αυτόματος προγραμματισμός:</strong> Τα canonical names εφαρμόζονται αυτόματα σε νέα τραγούδια 4 φορές/μέρα (08:15, 12:15, 16:15, 20:15)
      </div>
      
      {stats && (
        <div className="p-3 bg-teal-50 dark:bg-teal-900/20 rounded-lg border border-teal-200 dark:border-teal-800">
          <div className="text-sm text-teal-800 dark:text-teal-200">
            <p>✅ Database ενημερώθηκε!</p>
            <p className="mt-1">• Καλλιτέχνες: {stats.artists_added}</p>
            <p>• Συνολικές εγγραφές: {stats.total_entries}</p>
            {stats.songs_pending_canonical > 0 && (
              <p className="mt-2 text-orange-600 dark:text-orange-400 font-medium">
                ⚠️ {stats.songs_pending_canonical.toLocaleString()} τραγούδια χρειάζονται ακόμα canonical names
              </p>
            )}
            {stats.songs_pending_canonical === 0 && (
              <p className="mt-2 text-green-600 dark:text-green-400 font-medium">
                ✅ Όλα τα τραγούδια έχουν canonical names!
              </p>
            )}
          </div>
        </div>
      )}
      
      {/* Progress indicator during background task */}
      {applying && taskStatus && (
        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="text-sm text-blue-800 dark:text-blue-200">
            <div className="flex items-center gap-2 mb-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="font-medium">Επεξεργασία σε εξέλιξη...</span>
            </div>
            <p>{taskStatus.message}</p>
            {taskStatus.total > 0 && (
              <div className="mt-2">
                <div className="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${Math.min(100, (taskStatus.progress / taskStatus.total) * 100)}%` }}
                  />
                </div>
                <p className="text-xs mt-1">
                  {taskStatus.progress} / {taskStatus.total} ({Math.round((taskStatus.progress / taskStatus.total) * 100)}%)
                </p>
              </div>
            )}
          </div>
        </div>
      )}
      
      {applyStats && !applying && (
        <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
          <div className="text-sm text-purple-800 dark:text-purple-200">
            <p>✅ Τραγούδια ενημερώθηκαν!</p>
            <p className="mt-1">• Ενημερώθηκαν: {applyStats.updated}</p>
            <p>• Χωρίς canonical: {applyStats.total_without_canonical || 0}</p>
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// RADIO CHART SETTINGS - Draft/Public toggle
// ═══════════════════════════════════════════════════════════════
const RadioChartSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isPublic, setIsPublic] = useState(false);

  const getAuthHeaders = () => {
    const token = storage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/radio-chart/settings`, getAuthHeaders());
      setIsPublic(res.data.is_public || false);
    } catch (err) {
      console.error('Error fetching chart settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const togglePublic = async () => {
    setSaving(true);
    try {
      const newValue = !isPublic;
      await axios.put(
        `${BACKEND_URL}/api/radio-chart/settings?is_public=${newValue}`,
        {},
        getAuthHeaders()
      );
      setIsPublic(newValue);
      toast.success(newValue ? '📊 Radio Chart δημοσιεύτηκε!' : '📊 Radio Chart σε Draft mode');
    } catch (err) {
      toast.error('Σφάλμα: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  if (loading) {
    return <div className="flex items-center gap-2 text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> Φόρτωση...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between p-4 border rounded-lg bg-gray-50 dark:bg-gray-800">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${isPublic ? 'bg-green-100 dark:bg-green-900/30' : 'bg-orange-100 dark:bg-orange-900/30'}`}>
            {isPublic ? (
              <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
            ) : (
              <AlertCircle className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            )}
          </div>
          <div>
            <div className="font-medium text-gray-900 dark:text-white">
              Radio Chart Status
            </div>
            <div className="text-sm text-gray-500">
              {isPublic ? (
                <span className="text-green-600">✅ Public - Εμφανίζεται στο /radio-chart</span>
              ) : (
                <span className="text-orange-600">⏳ Draft - Δεν εμφανίζεται στο κοινό</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Switch
            checked={isPublic}
            onCheckedChange={togglePublic}
            disabled={saving}
          />
          <Badge variant={isPublic ? "default" : "secondary"}>
            {isPublic ? 'Public' : 'Draft'}
          </Badge>
        </div>
      </div>
      
      <p className="text-xs text-gray-500">
        💡 Όταν είναι Draft, η σελίδα /radio-chart εμφανίζει μήνυμα "Σύντομα διαθέσιμο".
        Μπορείτε να ολοκληρώσετε τις ρυθμίσεις πριν το δημοσιοποιήσετε.
      </p>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// MAIN ADMIN CONFIG SETTINGS COMPONENT
// ═══════════════════════════════════════════════════════════════
const AdminConfigSettings = () => {
  const [activeTab, setActiveTab] = useState('scoring');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Scoring config state
  const [scoringConfig, setScoringConfig] = useState(null);
  
  // Greek artists state
  const [greekArtists, setGreekArtists] = useState([]);
  const [newArtist, setNewArtist] = useState({ name: '', month: 1, day: 1, reason: '' });
  
  // Jingle patterns state
  const [jinglePatterns, setJinglePatterns] = useState([]);
  const [newPattern, setNewPattern] = useState('');
  
  // Export settings state
  const [exportSettings, setExportSettings] = useState(null);

  const getAuthHeaders = () => {
    const token = storage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  // Fetch all configurations
  const fetchScoringConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/config/scoring`, getAuthHeaders());
      setScoringConfig(res.data);
    } catch (err) {
      console.error('Error fetching scoring config:', err);
      toast.error('Σφάλμα φόρτωσης ρυθμίσεων scoring');
    }
  }, []);

  const fetchGreekArtists = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/config/greek-artists`, getAuthHeaders());
      setGreekArtists(res.data.artists || []);
    } catch (err) {
      console.error('Error fetching Greek artists:', err);
      toast.error('Σφάλμα φόρτωσης καλλιτεχνών');
    }
  }, []);

  const fetchJinglePatterns = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/config/jingle-patterns`, getAuthHeaders());
      setJinglePatterns(res.data.patterns || []);
    } catch (err) {
      console.error('Error fetching jingle patterns:', err);
      toast.error('Σφάλμα φόρτωσης jingle patterns');
    }
  }, []);

  const fetchExportSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/config/export-settings`, getAuthHeaders());
      setExportSettings(res.data);
    } catch (err) {
      console.error('Error fetching export settings:', err);
      toast.error('Σφάλμα φόρτωσης export settings');
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchScoringConfig(),
      fetchGreekArtists(),
      fetchJinglePatterns(),
      fetchExportSettings()
    ]).finally(() => setLoading(false));
  }, [fetchScoringConfig, fetchGreekArtists, fetchJinglePatterns, fetchExportSettings]);

  // Save scoring config
  const handleSaveScoringConfig = async () => {
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/config/scoring`, scoringConfig, getAuthHeaders());
      toast.success('Οι ρυθμίσεις scoring αποθηκεύτηκαν!');
    } catch (err) {
      toast.error('Σφάλμα αποθήκευσης: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  // Add Greek artist
  const handleAddArtist = async () => {
    if (!newArtist.name.trim()) {
      toast.error('Παρακαλώ εισάγετε όνομα καλλιτέχνη');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/config/greek-artists`, newArtist, getAuthHeaders());
      toast.success(`Ο καλλιτέχνης "${newArtist.name}" προστέθηκε!`);
      setNewArtist({ name: '', month: 1, day: 1, reason: '' });
      fetchGreekArtists();
    } catch (err) {
      toast.error('Σφάλμα προσθήκης: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  // Delete Greek artist
  const handleDeleteArtist = async (artist) => {
    if (!window.confirm(`Διαγραφή του "${artist.name}" (${artist.day}/${artist.month});`)) return;
    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/config/greek-artists/${encodeURIComponent(artist.name)}?month=${artist.month}&day=${artist.day}`,
        getAuthHeaders()
      );
      toast.success(`Ο καλλιτέχνης "${artist.name}" διαγράφηκε`);
      fetchGreekArtists();
    } catch (err) {
      toast.error('Σφάλμα διαγραφής: ' + (err.response?.data?.detail || err.message));
    }
  };

  // Import default Greek artists
  const handleImportDefaultArtists = async () => {
    if (!window.confirm('Θα εισαχθούν όλοι οι προεπιλεγμένοι Έλληνες καλλιτέχνες. Συνέχεια;')) return;
    setSaving(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/config/greek-artists/import-defaults`, {}, getAuthHeaders());
      toast.success(`Εισήχθησαν ${res.data.imported} καλλιτέχνες!`);
      fetchGreekArtists();
    } catch (err) {
      toast.error('Σφάλμα εισαγωγής: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  // Add jingle pattern
  const handleAddPattern = async () => {
    if (!newPattern.trim()) {
      toast.error('Παρακαλώ εισάγετε pattern');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/config/jingle-patterns`, { pattern: newPattern, enabled: true }, getAuthHeaders());
      toast.success(`Το pattern "${newPattern}" προστέθηκε!`);
      setNewPattern('');
      fetchJinglePatterns();
    } catch (err) {
      toast.error('Σφάλμα προσθήκης: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  // Delete jingle pattern
  const handleDeletePattern = async (pattern) => {
    if (!window.confirm(`Διαγραφή του pattern "${pattern.pattern}";`)) return;
    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/config/jingle-patterns/${encodeURIComponent(pattern.pattern)}`,
        getAuthHeaders()
      );
      toast.success(`Το pattern "${pattern.pattern}" διαγράφηκε`);
      fetchJinglePatterns();
    } catch (err) {
      toast.error('Σφάλμα διαγραφής: ' + (err.response?.data?.detail || err.message));
    }
  };

  // Save export settings
  const handleSaveExportSettings = async () => {
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/config/export-settings`, exportSettings, getAuthHeaders());
      toast.success('Οι ρυθμίσεις export αποθηκεύτηκαν!');
    } catch (err) {
      toast.error('Σφάλμα αποθήκευσης: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  // Backup all configs
  const handleBackup = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/config/backup`, getAuthHeaders());
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `config-backup-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Το backup κατέβηκε!');
    } catch (err) {
      toast.error('Σφάλμα backup: ' + (err.response?.data?.detail || err.message));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
        <span className="ml-2 text-gray-600 dark:text-gray-300">Φόρτωση ρυθμίσεων...</span>
      </div>
    );
  }

  const months = [
    'Ιαν', 'Φεβ', 'Μάρ', 'Απρ', 'Μάι', 'Ιούν',
    'Ιούλ', 'Αύγ', 'Σεπ', 'Οκτ', 'Νοέ', 'Δεκ'
  ];

  return (
    <div className="space-y-6" data-testid="admin-config-settings">
      {/* Header with backup button */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Settings className="h-5 w-5 text-purple-600" />
            Ρυθμίσεις Συστήματος
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Διαχειριστείτε τις παραμέτρους του Stream Monitor
          </p>
        </div>
        <Button variant="outline" onClick={handleBackup} data-testid="backup-config-btn">
          <Download className="h-4 w-4 mr-2" />
          Backup
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-8 bg-gray-100 dark:bg-gray-800">
          <TabsTrigger value="scoring" className="flex items-center gap-2" data-testid="tab-scoring">
            <Settings className="h-4 w-4" />
            <span className="hidden sm:inline">Scoring</span>
          </TabsTrigger>
          <TabsTrigger value="artists" className="flex items-center gap-2" data-testid="tab-artists">
            <Music className="h-4 w-4" />
            <span className="hidden sm:inline">Καλλιτέχνες</span>
          </TabsTrigger>
          <TabsTrigger value="jingles" className="flex items-center gap-2" data-testid="tab-jingles">
            <Radio className="h-4 w-4" />
            <span className="hidden sm:inline">Jingles</span>
          </TabsTrigger>
          <TabsTrigger value="years" className="flex items-center gap-2" data-testid="tab-years">
            <Database className="h-4 w-4" />
            <span className="hidden sm:inline">Έτη</span>
          </TabsTrigger>
          <TabsTrigger value="export" className="flex items-center gap-2" data-testid="tab-export">
            <Download className="h-4 w-4" />
            <span className="hidden sm:inline">Export</span>
          </TabsTrigger>
          <TabsTrigger value="chart" className="flex items-center gap-2" data-testid="tab-chart">
            <Radio className="h-4 w-4" />
            <span className="hidden sm:inline">Chart</span>
          </TabsTrigger>
          <TabsTrigger value="validation" className="flex items-center gap-2" data-testid="tab-validation">
            <AlertCircle className="h-4 w-4" />
            <span className="hidden sm:inline">Validation</span>
          </TabsTrigger>
          <TabsTrigger value="first-plays" className="flex items-center gap-2" data-testid="tab-first-plays">
            <Mail className="h-4 w-4" />
            <span className="hidden sm:inline">Πρώτες Μεταδ.</span>
          </TabsTrigger>
        </TabsList>

        {/* SCORING TAB */}
        <TabsContent value="scoring" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Settings className="h-5 w-5 text-purple-600" />
                Scoring System v2.0
              </CardTitle>
              <CardDescription>
                Ρυθμίστε τα κατώφλια βαθμολογίας και τους πολλαπλασιαστές
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {scoringConfig && (
                <>
                  {/* Category Thresholds */}
                  <div>
                    <Label className="text-sm font-medium">Κατηγορίες Βαθμολογίας</Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-2">
                      {scoringConfig.categories && Object.entries(scoringConfig.categories).map(([cat, config]) => (
                        <div key={cat} className="p-3 border rounded-lg dark:border-gray-700">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-lg">{config.emoji || '📊'}</span>
                            <span className="font-medium text-gray-900 dark:text-white">{cat}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Label className="text-xs text-gray-500">Min Score:</Label>
                            <Input
                              type="number"
                              value={config.min_score}
                              onChange={(e) => {
                                const newConfig = { ...scoringConfig };
                                newConfig.categories[cat].min_score = parseInt(e.target.value) || 0;
                                setScoringConfig(newConfig);
                              }}
                              className="w-20 h-8"
                              data-testid={`scoring-${cat.toLowerCase()}-min`}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Separator />

                  {/* Early Drop Multipliers */}
                  <div>
                    <Label className="text-sm font-medium">Early Drop Multipliers</Label>
                    <p className="text-xs text-gray-500 mb-2">
                      Πολλαπλασιαστές βάσει ποσοστού απώλειας στο πρώτο λεπτό
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-2">
                      {scoringConfig.early_drop_multipliers && Object.entries(scoringConfig.early_drop_multipliers).map(([level, config]) => (
                        <div key={level} className="p-3 border rounded-lg dark:border-gray-700">
                          <div className="font-medium text-gray-900 dark:text-white capitalize mb-2">{level}</div>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <Label className="text-xs text-gray-500 w-16">Threshold:</Label>
                              <Input
                                type="number"
                                step="0.01"
                                value={config.threshold}
                                onChange={(e) => {
                                  const newConfig = { ...scoringConfig };
                                  newConfig.early_drop_multipliers[level].threshold = parseFloat(e.target.value) || 0;
                                  setScoringConfig(newConfig);
                                }}
                                className="w-20 h-8"
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <Label className="text-xs text-gray-500 w-16">Multiplier:</Label>
                              <Input
                                type="number"
                                step="0.01"
                                value={config.multiplier}
                                onChange={(e) => {
                                  const newConfig = { ...scoringConfig };
                                  newConfig.early_drop_multipliers[level].multiplier = parseFloat(e.target.value) || 0;
                                  setScoringConfig(newConfig);
                                }}
                                className="w-20 h-8"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Separator />

                  {/* Other Settings */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-sm">Ημέρες Προστασίας Νέων Τραγουδιών</Label>
                      <Input
                        type="number"
                        value={scoringConfig.new_song_protection_days || 14}
                        onChange={(e) => setScoringConfig({
                          ...scoringConfig,
                          new_song_protection_days: parseInt(e.target.value) || 14
                        })}
                        className="mt-1"
                        data-testid="new-song-protection-days"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Quality Signal Retention Threshold</Label>
                      <Input
                        type="number"
                        value={scoringConfig.quality_signal_retention_threshold || 90}
                        onChange={(e) => setScoringConfig({
                          ...scoringConfig,
                          quality_signal_retention_threshold: parseInt(e.target.value) || 90
                        })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Quality Signal Early Drop Threshold</Label>
                      <Input
                        type="number"
                        value={scoringConfig.quality_signal_early_drop_threshold || 3}
                        onChange={(e) => setScoringConfig({
                          ...scoringConfig,
                          quality_signal_early_drop_threshold: parseInt(e.target.value) || 3
                        })}
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end pt-4">
                    <Button onClick={handleSaveScoringConfig} disabled={saving} data-testid="save-scoring-btn">
                      {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                      Αποθήκευση Scoring
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ARTISTS TAB */}
        <TabsContent value="artists" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Music className="h-5 w-5 text-purple-600" />
                    Έλληνες Καλλιτέχνες
                  </CardTitle>
                  <CardDescription>
                    Ημερομηνίες επετείων για ειδικές ενθέσεις
                  </CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={handleImportDefaultArtists} disabled={saving}>
                  <Upload className="h-4 w-4 mr-2" />
                  Εισαγωγή Defaults
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Add new artist form */}
              <div className="p-4 border rounded-lg dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                <Label className="text-sm font-medium mb-3 block">Προσθήκη Νέου Καλλιτέχνη</Label>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                  <div className="md:col-span-2">
                    <Input
                      placeholder="Όνομα καλλιτέχνη"
                      value={newArtist.name}
                      onChange={(e) => setNewArtist({ ...newArtist, name: e.target.value })}
                      data-testid="new-artist-name"
                    />
                  </div>
                  <div>
                    <select
                      className="w-full h-10 px-3 border rounded-md dark:bg-gray-800 dark:border-gray-700"
                      value={newArtist.month}
                      onChange={(e) => setNewArtist({ ...newArtist, month: parseInt(e.target.value) })}
                    >
                      {months.map((m, i) => (
                        <option key={i} value={i + 1}>{m}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Input
                      type="number"
                      min="1"
                      max="31"
                      placeholder="Ημέρα"
                      value={newArtist.day}
                      onChange={(e) => setNewArtist({ ...newArtist, day: parseInt(e.target.value) || 1 })}
                    />
                  </div>
                  <div>
                    <Button onClick={handleAddArtist} disabled={saving} className="w-full" data-testid="add-artist-btn">
                      <Plus className="h-4 w-4 mr-1" />
                      Προσθήκη
                    </Button>
                  </div>
                </div>
              </div>

              {/* Artists list */}
              <div className="border rounded-lg dark:border-gray-700">
                <div className="p-3 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                    Σύνολο: {greekArtists.length} καλλιτέχνες
                  </span>
                  <Badge variant="outline">
                    {greekArtists.filter(a => a.source === 'database' || a.source === 'admin_panel').length} custom
                  </Badge>
                </div>
                <ScrollArea className="h-[300px]">
                  <div className="divide-y dark:divide-gray-700">
                    {greekArtists.map((artist, idx) => {
                      // Handle both formats: name as string OR as object with nested name
                      const artistName = typeof artist.name === 'object' ? artist.name.name : artist.name;
                      const artistDescription = typeof artist.name === 'object' ? artist.name.description : artist.reason;
                      const artistYear = typeof artist.name === 'object' ? artist.name.year : artist.year;
                      
                      return (
                        <div key={idx} className="p-3 flex justify-between items-center hover:bg-gray-50 dark:hover:bg-gray-800/50">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 font-bold text-sm">
                              {artist.day}/{artist.month}
                            </div>
                            <div>
                              <div className="font-medium text-gray-900 dark:text-white">{artistName}</div>
                              <div className="text-xs text-gray-500">
                                {artistYear && <span className="mr-2">({artistYear})</span>}
                                {artistDescription ? (artistDescription.length > 60 ? artistDescription.substring(0, 60) + '...' : artistDescription) : 'Επέτειος'}
                                {artist.source === 'hardcoded' && (
                                  <Badge variant="secondary" className="ml-2 text-xs">Default</Badge>
                                )}
                              </div>
                            </div>
                          </div>
                          {artist.source !== 'hardcoded' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteArtist({ ...artist, name: artistName })}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* JINGLES TAB */}
        <TabsContent value="jingles" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Radio className="h-5 w-5 text-purple-600" />
                Jingle Patterns
              </CardTitle>
              <CardDescription>
                Patterns για αναγνώριση και φιλτράρισμα jingles/spots
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Add new pattern */}
              <div className="flex gap-3">
                <Input
                  placeholder="Νέο pattern (π.χ. jingle, spot, promo)"
                  value={newPattern}
                  onChange={(e) => setNewPattern(e.target.value)}
                  className="flex-1"
                  data-testid="new-jingle-pattern"
                />
                <Button onClick={handleAddPattern} disabled={saving} data-testid="add-pattern-btn">
                  <Plus className="h-4 w-4 mr-1" />
                  Προσθήκη
                </Button>
              </div>

              {/* Patterns list */}
              <div className="border rounded-lg dark:border-gray-700">
                <div className="p-3 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                    Σύνολο: {jinglePatterns.length} patterns
                  </span>
                </div>
                <ScrollArea className="h-[300px]">
                  <div className="p-3 flex flex-wrap gap-2">
                    {jinglePatterns.map((pattern, idx) => (
                      <Badge
                        key={idx}
                        variant={pattern.source === 'hardcoded' ? 'secondary' : 'default'}
                        className="px-3 py-1.5 flex items-center gap-2"
                      >
                        <span>{pattern.pattern}</span>
                        {pattern.source !== 'hardcoded' && (
                          <button
                            onClick={() => handleDeletePattern(pattern)}
                            className="hover:text-red-500 ml-1"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        )}
                      </Badge>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* YEARS TAB - Song Year Management */}
        <TabsContent value="years" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Database className="h-5 w-5 text-purple-600" />
                Διαχείριση Ετών Τραγουδιών
              </CardTitle>
              <CardDescription>
                Αναζήτηση και διόρθωση ετών κυκλοφορίας
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <SongYearManager />
              
              {/* 2020 Years Cleanup Section */}
              <Separator className="my-6" />
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-orange-500" />
                  <h3 className="font-medium text-gray-900 dark:text-white">Καθαρισμός Λάθος Ετών 2020</h3>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  <strong>ΣΗΜΕΙΩΣΗ:</strong> Η αυτόματη προστασία από 2020 είναι πλέον ενεργή! 
                  Νέες εγγραφές με έτος 2020 από APIs απορρίπτονται αυτόματα.
                  Χρησιμοποιήστε αυτό το κουμπί μόνο για καθαρισμό παλαιών εγγραφών.
                </p>
                <YearCleanupButton />
              </div>
              
              {/* Canonical Names Section - NEW */}
              <Separator className="my-6" />
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <RefreshCw className="h-5 w-5 text-teal-500" />
                  <h3 className="font-medium text-gray-900 dark:text-white">Ενημέρωση Canonical Names (SoundCharts Format)</h3>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Ενημερώνει τη μορφή εμφάνισης καλλιτεχνών/τραγουδιών από τη βάση Εμπλουτισμού (user_songs_db.json).
                  Μετατρέπει τα ονόματα σε SoundCharts format (Title Case Greeklish).
                </p>
                <CanonicalNamesButton />
              </div>
              
            </CardContent>
          </Card>
        </TabsContent>

        {/* EXPORT TAB */}
        <TabsContent value="export" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Download className="h-5 w-5 text-purple-600" />
                Ρυθμίσεις Export
              </CardTitle>
              <CardDescription>
                Ρυθμίστε τι περιλαμβάνεται στα Excel exports
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {exportSettings && (
                <>
                  <div className="space-y-4">
                    <Label className="text-sm font-medium">Στήλες Advanced Metrics</Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-center justify-between p-3 border rounded-lg dark:border-gray-700">
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white">Early Drop %</div>
                          <div className="text-xs text-gray-500">Ποσοστό απώλειας στο πρώτο λεπτό</div>
                        </div>
                        <Switch
                          checked={exportSettings.include_early_drop}
                          onCheckedChange={(checked) => setExportSettings({
                            ...exportSettings,
                            include_early_drop: checked
                          })}
                          data-testid="export-early-drop-switch"
                        />
                      </div>
                      <div className="flex items-center justify-between p-3 border rounded-lg dark:border-gray-700">
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white">LSI</div>
                          <div className="text-xs text-gray-500">Listener Stability Index</div>
                        </div>
                        <Switch
                          checked={exportSettings.include_lsi}
                          onCheckedChange={(checked) => setExportSettings({
                            ...exportSettings,
                            include_lsi: checked
                          })}
                          data-testid="export-lsi-switch"
                        />
                      </div>
                      <div className="flex items-center justify-between p-3 border rounded-lg dark:border-gray-700">
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white">Curve Retention</div>
                          <div className="text-xs text-gray-500">Area Under Curve retention</div>
                        </div>
                        <Switch
                          checked={exportSettings.include_curve_retention}
                          onCheckedChange={(checked) => setExportSettings({
                            ...exportSettings,
                            include_curve_retention: checked
                          })}
                        />
                      </div>
                      <div className="flex items-center justify-between p-3 border rounded-lg dark:border-gray-700">
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white">Advanced Metrics</div>
                          <div className="text-xs text-gray-500">Όλα τα metrics v2.0</div>
                        </div>
                        <Switch
                          checked={exportSettings.include_advanced_metrics}
                          onCheckedChange={(checked) => setExportSettings({
                            ...exportSettings,
                            include_advanced_metrics: checked
                          })}
                        />
                      </div>
                    </div>
                  </div>

                  <Separator />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm">Date Format</Label>
                      <Input
                        value={exportSettings.date_format || '%d/%m %H:%M'}
                        onChange={(e) => setExportSettings({
                          ...exportSettings,
                          date_format: e.target.value
                        })}
                        className="mt-1"
                        placeholder="%d/%m %H:%M"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Timezone</Label>
                      <Input
                        value={exportSettings.timezone || 'Europe/Athens'}
                        onChange={(e) => setExportSettings({
                          ...exportSettings,
                          timezone: e.target.value
                        })}
                        className="mt-1"
                        placeholder="Europe/Athens"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end pt-4">
                    <Button onClick={handleSaveExportSettings} disabled={saving} data-testid="save-export-btn">
                      {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                      Αποθήκευση Export Settings
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* RADIO CHART TAB */}
        <TabsContent value="chart" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Radio className="h-5 w-5 text-purple-600" />
                Radio Chart Settings
              </CardTitle>
              <CardDescription>
                Ρυθμίσεις για το δημόσιο Radio Chart (Top 300)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <RadioChartSettings />
              
              <Separator className="my-6" />
              
              <div className="space-y-3">
                <h4 className="font-medium text-sm text-gray-700 dark:text-gray-300">
                  📻 Σταθμοί στο Chart
                </h4>
                <p className="text-xs text-gray-500">
                  Για να συμπεριληφθεί ένας σταθμός στο Radio Chart, επεξεργαστείτε τον σταθμό 
                  στο Stream Monitor και ενεργοποιήστε το checkbox "Συμμετοχή στο Radio Chart".
                </p>
                <a 
                  href="/admin/stream-monitor" 
                  className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1"
                >
                  → Μετάβαση στο Stream Monitor
                </a>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* VALIDATION TAB */}
        <TabsContent value="validation" className="mt-4">
          <ValidationPanel token={storage.getItem('token')} />
        </TabsContent>

        <TabsContent value="first-plays" className="mt-4">
          <FirstPlayAlertsPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminConfigSettings;
